import React, { Fragment, useCallback, useEffect, useState }  from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import ModalAdmin from '../../Components/ModalAdmin';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';
import SeccionPanel from '../../Components/SeccionPanel';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';
import useStorage from '../../Hooks/useStorage';

import reinscripcionHelper from '../../Helpers/ReinscripcionVista';

const REINSCRIPCION_ID = '6320e883b8bb93da171603ba';

const secciones = [
    {
        titulo: 'Fechas de reinscripción',
        campos: [
            { campo: 'fecha_limite_reinscripcion', etiqueta: 'Fecha límite de reinscripción' },
            { campo: 'dia_limite_colegiatura', etiqueta: 'Día límite de pago de colegiatura', numero: true, min: 1, max: 31 },
            { campo: 'calendario_vigente', etiqueta: 'Calendario vigente de los precios' },
            { campo: 'dias_pago_contado', etiqueta: 'Días naturales para pagar de contado', numero: true }
        ]
    },
    {
        titulo: 'Cine, Animación y Multimedia',
        campos: [
            { campo: 'cine_reinscripcion', etiqueta: 'Reinscripción hasta la fecha límite', numero: true },
            { campo: 'cine_reinscripcion_tardia', etiqueta: 'Reinscripción después de la fecha límite', numero: true },
            { campo: 'cine_colegiatura', etiqueta: 'Colegiatura antes del día límite', numero: true },
            { campo: 'cine_colegiatura_tardia', etiqueta: 'Colegiatura después del día límite', numero: true },
            { campo: 'cine_total_colegiaturas', etiqueta: 'Total de las 4 colegiaturas', numero: true },
            { campo: 'cine_total_contado', etiqueta: 'Total de contado', numero: true }
        ]
    },
    {
        titulo: 'Marketing Digital',
        campos: [
            { campo: 'marketing_reinscripcion', etiqueta: 'Reinscripción hasta la fecha límite', numero: true },
            { campo: 'marketing_reinscripcion_tardia', etiqueta: 'Reinscripción después de la fecha límite', numero: true },
            { campo: 'marketing_colegiatura', etiqueta: 'Colegiatura antes del día límite', numero: true },
            { campo: 'marketing_colegiatura_tardia', etiqueta: 'Colegiatura después del día límite', numero: true },
            { campo: 'marketing_total_colegiaturas', etiqueta: 'Total de las 4 colegiaturas', numero: true },
            { campo: 'marketing_total_contado', etiqueta: 'Total de contado', numero: true }
        ]
    },
    {
        titulo: 'Horario especial y reingreso',
        campos: [
            { campo: 'horario_presencial_fechas', etiqueta: 'Horario en persona: fechas' },
            { campo: 'horario_presencial_horas', etiqueta: 'Horario en persona: horas' },
            { campo: 'horario_en_linea', etiqueta: 'Horario en línea' },
            { campo: 'fecha_limite_cita_virtual', etiqueta: 'Fecha límite para pedir cita virtual' },
            { campo: 'reingreso_fecha_limite', etiqueta: 'Reingreso: reinscribirse antes del' },
            { campo: 'reingreso_fecha_respuesta', etiqueta: 'Reingreso: correo a más tardar el' }
        ]
    },
    {
        titulo: 'Materia a repetir',
        campos: [
            { campo: 'materia_repetir_costo', etiqueta: 'Costo en las fechas señaladas', numero: true },
            { campo: 'materia_repetir_tardio_1_fechas', etiqueta: 'Primer periodo tardío: fechas y horario' },
            { campo: 'materia_repetir_tardio_1_costo', etiqueta: 'Primer periodo tardío: costo', numero: true },
            { campo: 'materia_pendiente_tardio_1_cargo', etiqueta: 'Primer periodo tardío: cargo por materias pendientes', numero: true },
            { campo: 'materia_repetir_tardio_2_fechas', etiqueta: 'Segundo periodo tardío: fechas y horario' },
            { campo: 'materia_repetir_tardio_2_costo', etiqueta: 'Segundo periodo tardío: costo', numero: true },
            { campo: 'materia_pendiente_tardio_2_cargo', etiqueta: 'Segundo periodo tardío: cargo por materias pendientes', numero: true }
        ]
    },
    {
        titulo: 'Otros',
        campos: [
            { campo: 'costo_extraordinario', etiqueta: 'Costo de examen extraordinario', numero: true }
        ]
    }
];

const Reinscripcion = props => {
    const { editar, setEditar, recargar } = useAuth();

    const [reinscripcion, setReinscripcion] = useState(null);
    const [seccionActiva, setSeccionActiva] = useState(null);

    const [modalPdf, setModalPdf] = useState(false);
    const [archivoPdf, setArchivoPdf] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    const [consulta, setConsulta] = useState('');
    const [respuestaStorage] = useStorage(consulta);

    const horarioEspecialPdf = reinscripcion?.horario_especial_pdf ?? '';

    useEffect(() => {
        obtenerDatos();
    }, []);

    useEffect(() => {
        if (respuestaStorage.action === 'agregarPdf') guardarPdf(respuestaStorage.url);
        if (respuestaStorage.action === 'eliminarSinConfirmar') setConsulta('');
    }, [respuestaStorage]);

    const obtenerDatos = async () => {
        const res = await reinscripcionHelper.obtenerLaReinscripcion(REINSCRIPCION_ID);
        if (res.success) {
            setReinscripcion(res.data);
            setEditar(res.data);
        } else {
            Swal.fire('Error!', 'No se pudieron obtener las fechas y costos, codigo: ' + res.code, 'error');
        }
    }

    // Al cerrar el panel se limpia `editar`; entonces el iframe vuelve a mostrar lo guardado
    const borrador = editar._id ? editar : reinscripcion;
    const hayCambios = Boolean(editar._id && reinscripcion) && JSON.stringify(editar) !== JSON.stringify(reinscripcion);

    const panel = usePanelEdicion({ hayCambios, onCerrar: () => setEditar({}) });

    const abrirPanel = () => {
        if (!editar._id && reinscripcion) setEditar(reinscripcion);
        panel.abrir('editar');
    }

    const descartar = async () => {
        if (await confirmarDescartar()) setEditar(reinscripcion);
    }

    const seleccionarCampo = ({ id }) => {
        if (id === 'horario_especial_pdf') return setModalPdf(true);
        abrirPanel();
        document.getElementById(id)?.focus();
    }

    const { iframeRef, src } = usePreviewSitio({
        page: 'reinscripcion',
        ruta: '/reinscripcion',
        data: borrador,
        onSelect: seleccionarCampo
    });

    const leerDato = e => {
        const { name, value, type } = e.target;
        setEditar(editar => ({ ...editar, [name]: type === 'number' && value !== '' ? Number(value) : value }));
    }

    const actualizar = async e => {
        e.preventDefault();
        const faltantes = secciones.flatMap(seccion => seccion.campos).filter(({ campo }) => editar[campo] === '');
        if (faltantes.length) return Swal.fire('Error!', 'Faltan datos por llenar: ' + faltantes.map(({ etiqueta }) => etiqueta).join(', '), 'error');
        if ((await reinscripcionHelper.editarReinscripcion(editar, REINSCRIPCION_ID))?.success) recargar();
    }

    const cerrarModalPdf = useCallback(() => {
        setModalPdf(false);
        setArchivoPdf(null);
    }, []);

    const subirPdf = e => {
        e.preventDefault();
        if (!archivoPdf) return Swal.fire('Error!', 'Selecciona un archivo PDF', 'error');
        setSubiendo(true);
        setConsulta({ consulta: 'agregarPdf', name: 'horario_especial_' + Date.now(), folder: 'pdfs/reinscripcion/', file: archivoPdf, metadata: { contentDisposition: 'attachment; filename="horario-especial-caav.pdf"' } });
    }

    const guardarPdf = async url => {
        // useStorage solo vuelve a ejecutarse si cambia el texto de la consulta
        setConsulta('');
        if (!url) {
            setSubiendo(false);
            return Swal.fire('Error!', 'No se pudo subir el archivo', 'error');
        }
        const anterior = horarioEspecialPdf;
        const res = await reinscripcionHelper.guardarHorarioEspecialPdf(REINSCRIPCION_ID, url);
        setSubiendo(false);
        if (res.success) {
            // Se borra hasta que el documento ya apunta al nuevo, para que el sitio nunca quede con un enlace roto
            if (anterior.includes('/o/pdfs%2Freinscripcion%2F')) {
                setConsulta({ consulta: 'eliminarSinConfirmar', urlFile: anterior });
            }
            const cambios = { horario_especial_pdf: url, updatedAt: res.data?.updatedAt ?? new Date().toISOString() };
            setReinscripcion(reinscripcion => ({ ...reinscripcion, ...cambios }));
            setEditar(editar => (editar._id ? { ...editar, ...cambios } : editar));
            cerrarModalPdf();
        }
    }

    return (
        <Fragment>
            <SideNav />
            {subiendo && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo='Editar fechas y costos'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    secciones={secciones.map((seccion, i) => ({ id: String(i), titulo: seccion.titulo }))}
                    seccionActiva={seccionActiva}
                    onSeccion={setSeccionActiva}
                    hayCambios={hayCambios}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={descartar} disabled={!hayCambios}>
                                Descartar
                            </button>
                            <button type='submit' form='form_reinscripcion' className='boton_admin' disabled={!hayCambios}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    <form id='form_reinscripcion' onSubmit={actualizar}>
                        {secciones.map((seccion, i) => (
                            <SeccionPanel
                                key={seccion.titulo}
                                id={String(i)}
                                titulo={seccion.titulo}
                                activa={seccionActiva === String(i)}
                                onFocus={() => setSeccionActiva(String(i))}
                            >
                                {seccion.campos.map(({ campo, etiqueta, numero, min = 0, max }) => (
                                    <div className="input-field" key={campo}>
                                        <input
                                            onChange={leerDato}
                                            value={editar[campo] ?? ''}
                                            id={campo}
                                            name={campo}
                                            type={numero ? 'number' : 'text'}
                                            min={numero ? min : undefined}
                                            max={max}
                                            className="validate"
                                        />
                                        <label className='active' for={campo}>{etiqueta}</label>
                                    </div>
                                ))}
                            </SeccionPanel>
                        ))}
                    </form>
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo='Vista previa de reinscripción'
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Reinscripción'
                    ayuda='Haz clic en una fecha, un costo o un horario para editarlo'
                    acciones={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={() => setModalPdf(true)}>
                                PDF de horario especial
                            </button>
                            <button type='button' className='boton_admin' onClick={abrirPanel}>
                                Editar fechas y costos
                            </button>
                        </Fragment>
                    }
                >
                    <ModalAdmin abierto={modalPdf} onCerrar={cerrarModalPdf} titulo='PDF de horario especial'>
                        {horarioEspecialPdf ? (
                            <p>
                                <a href={horarioEspecialPdf} target="_blank" rel="noopener noreferrer">Descargar PDF actual</a>
                                {reinscripcion?.updatedAt && (
                                    <span style={{color:"#757575"}}> · actualizado el {new Date(reinscripcion.updatedAt).toLocaleDateString('es-MX', { day:'numeric', month:'long', year:'numeric' })}</span>
                                )}
                            </p>
                        ) : (
                            <p style={{color:"#757575"}}>Aún no se ha subido un PDF; el sitio usa el archivo incluido en el código.</p>
                        )}
                        <form onSubmit={subirPdf}>
                            <div className="file-field input-field">
                                <div className="btn" style={{backgroundColor:"#00496C"}}>
                                    <span>Archivo</span>
                                    <input onChange={e => setArchivoPdf(e.target.files[0] ?? null)} type="file" accept="application/pdf" />
                                </div>
                                <div className="file-path-wrapper">
                                    <input className="file-path" type="text" placeholder="Selecciona un PDF" value={archivoPdf?.name ?? ''} readOnly />
                                </div>
                            </div>
                            {horarioEspecialPdf && (
                                <p style={{color:"#757575", fontSize:"13px", margin:"0"}}>Al reemplazarlo, el PDF anterior se elimina y el sitio descargará el nuevo.</p>
                            )}
                            <div style={{display:"flex", justifyContent:"flex-end", gap:"10px", marginTop:"10px"}}>
                                <button type='button' className='boton_admin boton_admin_secundario' onClick={cerrarModalPdf}>
                                    Cancelar
                                </button>
                                <button type='submit' className='boton_admin' disabled={!archivoPdf}>
                                    {horarioEspecialPdf ? 'Reemplazar PDF' : 'Subir PDF'}
                                </button>
                            </div>
                        </form>
                    </ModalAdmin>
                </PreviewSitio>
            </div>
        </Fragment>
    );
}

export default Reinscripcion;
